/**
 * Settings > Data > "Reply Labels" — management table for the company's
 * reply-thread labels (FIR-7631). Sibling of TagTable (same table/modal
 * structure) but backed by the emailbison reply-label hooks, which share
 * query keys with the inbox Add-label menu so both surfaces stay in sync.
 */
import { zodResolver } from '@hookform/resolvers/zod'
import { createColumnHelper } from '@tanstack/react-table'
import to from 'await-to-js'
import { z } from 'zod'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Controller, useForm } from 'react-hook-form'
import { PiMagnifyingGlass, PiPlus } from 'react-icons/pi'

import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import { DataTable, DataTableProvider } from 'components/DataTable'
import dialogConfirm from 'components/dialogConfirm'
import * as CommonS from 'components/Tables/CommonTable.styles'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { DataTableContainer } from 'components/DataTable/UI'
import FbButton from 'components/FbUI/FbButton'
import { TextInput } from 'components/FormUtils/TextInput'
import { Modal } from 'components/UI/Modal/Modal'
import { cn } from 'components/UI/cn'
import {
  ColorDot,
  LABEL_PALETTE,
  LabelPill,
  PALETTE_KEYS,
  toastApiError,
} from 'features/campaigns/InboxLabels'
import {
  type ApiReplyLabel,
  useCreateReplyLabel,
  useDeleteReplyLabel,
  useGetReplyLabels,
  useUpdateReplyLabel,
} from 'features/campaigns/replies_api'

const TABLE_KEY = 'reply-label-table'

const formSchema = z.object({
  name: z
    .string()
    .min(1, 'Label name is required')
    .max(50, 'Label name must be 50 characters or less'),
  color: z.string().min(1),
})

type FormSchema = z.infer<typeof formSchema>

const defaultValues: FormSchema = { name: '', color: 'blue' }

function ReplyLabelTableContent() {
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedLabelId, setSelectedLabelId] = useState<number>()
  const [searchQuery, setSearchQuery] = useState('')

  const { data, isFetching } = useGetReplyLabels()
  const createMutation = useCreateReplyLabel()
  const updateMutation = useUpdateReplyLabel()
  const deleteMutation = useDeleteReplyLabel()

  const rows = useMemo(() => {
    const labels = data ?? []
    const query = searchQuery.trim().toLowerCase()
    if (!query) return labels
    return labels.filter((l) => l.name.toLowerCase().includes(query))
  }, [data, searchQuery])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
  } = useForm<FormSchema>({
    defaultValues,
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  })

  function hideModal() {
    setShowModal(false)
    reset(defaultValues)
    setSelectedLabelId(undefined)
  }

  const onSubmit = handleSubmit(async (values) => {
    const [err] = await to(
      selectedLabelId
        ? updateMutation.mutateAsync({
            id: selectedLabelId,
            name: values.name,
            color: values.color,
          })
        : createMutation.mutateAsync({
            name: values.name,
            color: values.color,
          })
    )
    if (err) {
      toastApiError(
        err,
        selectedLabelId ? 'Error updating label' : 'Error creating label'
      )
      return
    }
    toast.success(selectedLabelId ? 'Label updated' : 'Label created')
    hideModal()
  })

  function handleRowEdit(row: ApiReplyLabel) {
    setSelectedLabelId(row.id)
    reset({ name: row.name, color: row.color })
    setShowModal(true)
  }

  async function handleRowDelete(row: ApiReplyLabel) {
    if (
      await dialogConfirm(
        'Deleting this label removes it from every reply thread it is applied to. Are you sure?',
        'Delete Label'
      )
    ) {
      const [err] = await to(deleteMutation.mutateAsync(row.id))
      if (err) {
        toastApiError(err, 'Error deleting label')
        return
      }
      toast.success('Label deleted')
    }
  }

  const columnHelper = createColumnHelper<ApiReplyLabel>()

  return (
    <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
      <DataTableContainer className="h-full">
        <DataTable
          tableHeader={
            <CommonS.SearchContainer>
              <CommonS.SearchContainerFilters>
                <CommonS.TableSearchContainer>
                  <div className="relative w-full">
                    <PiMagnifyingGlass
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search reply labels..."
                      aria-label="Search reply labels"
                      className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                    />
                  </div>
                </CommonS.TableSearchContainer>
                <CommonS.TableButtonsContainer>
                  <FbButton
                    variant="primary"
                    size="sm"
                    onClick={() => setShowModal(true)}
                    className="!bg-brand-bright !text-white hover:!bg-brand-active"
                  >
                    <PiPlus size={16} />
                    Create Reply Label
                  </FbButton>
                </CommonS.TableButtonsContainer>
              </CommonS.SearchContainerFilters>
            </CommonS.SearchContainer>
          }
          tableKey={TABLE_KEY}
          data={rows}
          columns={[
            columnHelper.accessor('name', {
              header: 'Name',
              size: 280,
              meta: { isFirstColumn: true },
              cell: (info) => <LabelPill label={info.row.original} />,
            }),
            columnHelper.accessor('color', {
              header: 'Color',
              size: 160,
              cell: (info) => (
                <span className="inline-flex items-center gap-2 text-sm text-gray-700 capitalize">
                  <ColorDot color={info.getValue()} />
                  {info.getValue()}
                </span>
              ),
            }),
            columnHelper.display({
              id: 'actions',
              header: () => <div className="w-full text-center">Actions</div>,
              size: 70,
              meta: {
                isLastColumn: true,
                headerDisableOrdering: true,
              },
              cell: ({ row }) => (
                <ActionsDropdown
                  className="!w-full justify-center"
                  actions={[
                    createEditAction(() => handleRowEdit(row.original)),
                    createDeleteAction(() => handleRowDelete(row.original)),
                  ]}
                />
              ),
            }),
          ]}
          sortableFields={[]}
          isPaginationEnabled={false}
          loading={isFetching}
          stickyLastColumn={true}
          enableColumnResizing
          rowHeight={44}
          headerHeight={40}
        />
      </DataTableContainer>
      <Modal
        open={showModal}
        title={selectedLabelId ? 'Edit Reply Label' : 'Add Reply Label'}
        description="Labels organize reply threads in the campaign inbox (for example: Deal Created, Waiting on more info)."
        onOpenChange={(open) => !open && hideModal()}
        loading={isSubmitting}
        blockAccept={!isValid}
        onAccept={onSubmit}
        acceptButtonText={selectedLabelId ? 'Update' : 'Create'}
      >
        <TextInput
          name="name"
          control={control}
          type="text"
          label="Label Name"
          placeholder="Waiting on more info"
        />
        <Controller
          control={control}
          name="color"
          render={({ field }) => (
            <div>
              <span className="mb-1.5 block text-sm font-medium text-fg">
                Color
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {PALETTE_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => field.onChange(key)}
                    aria-label={`Color ${key}`}
                    className={cn(
                      'size-6 rounded-full ring-offset-1 transition',
                      LABEL_PALETTE[key].dot,
                      field.value === key
                        ? 'ring-2 ring-brand'
                        : 'hover:opacity-80'
                    )}
                  />
                ))}
              </div>
            </div>
          )}
        />
      </Modal>
    </div>
  )
}

export function ReplyLabelTable() {
  return (
    <ColumnsStoreProvider tableKey={TABLE_KEY}>
      <DataTableProvider tableKey={TABLE_KEY}>
        <ReplyLabelTableContent />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
